// FILE: src/components/session/reviewModel.js
//
// The normalized shape the shared review UI renders, and the two adapters that
// produce it:
//
//   buildLiveReviewModel()        — the session that just ended (in-memory)
//   buildHistoricalReviewModel()  — a stored attempt, from get_session_review
//
// HISTORICAL INTEGRITY RULE (migration 097): for a stored attempt, grading is
// immutable and presentation is best-effort.
//   • the score and counts come from exam_sessions exactly as written at
//     completion — never recomputed from today's question bank
//   • per-question correctness comes from question_attempts.is_correct
//   • the "correct answer" shown is the key the attempt was GRADED against
//     (exam_sessions.question_summary), with any later key change surfaced
//   • wording/choices/explanation are the attempt-time version where it can be
//     reconstructed; otherwise the item says so (contentStatus)
import { normalizeSupabaseQuestion } from '../../services/questionService';

/** Stable, order-insensitive comparison of two answer-key arrays. */
export function sameKey(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  const sa = [...a].map(String).sort();
  const sb = [...b].map(String).sort();
  return sa.every((v, i) => v === sb[i]);
}

/**
 * Live model — the session that just ended.
 *
 * @param {object} p
 * @param {object} p.session           App.js session descriptor
 * @param {object} p.results           computeResults(...) output
 * @param {object} p.questionStates    { [index]: { timeSpentSeconds, ... } }
 * @param {Set}    [p.flaggedIndices]
 * @param {string} p.completionReason
 * @param {number} [p.timeUsedSeconds] exam-clock seconds consumed
 */
export function buildLiveReviewModel({
  session, results, questionStates = {}, flaggedIndices, completionReason, timeUsedSeconds,
}) {
  const items = results.items.map((r) => ({
    index: r.index,
    order: r.index,
    question: r.question,
    referenceId: r.question?.referenceId || null,
    selectedAnswers: r.selectedAnswers || [],
    status: r.status,
    isExperimental: r.isExperimental === true,
    timeSpentSeconds: typeof questionStates[r.index]?.timeSpentSeconds === 'number'
      ? Math.round(questionStates[r.index].timeSpentSeconds)
      : null,
    wasFlagged: flaggedIndices ? flaggedIndices.has(r.index) : false,
    caseStudyId: r.question?.caseStudyId || null,
    contentStatus: null,      // live content is by definition what the learner saw
    contentChanged: false,
    keyChanged: false,
    currentKey: null,
  }));

  return {
    mode: 'live',
    summary: {
      sessionId: null,
      examId: session.examId,
      displayName: session.displayName,
      modeName: session.modeName,
      mode: session.mode,
      completionReason,
      startedAt: session.startedAt ? new Date(session.startedAt).toISOString() : null,
      endedAt: null,
      totalQuestions: session.totalQuestions,
      answered: results.correct + results.incorrect,
      correct: results.correct,
      incorrect: results.incorrect,
      skipped: results.skipped,
      unanswered: results.unanswered,
      total: results.total,
      percentage: results.percentage,
      hasExperimental: results.hasExperimental,
      durationMinutes: session.durationMinutes || null,
      ...deriveTimeUsed({
        storedSeconds: typeof timeUsedSeconds === 'number' ? Math.round(timeUsedSeconds) : null,
        items,
        completionReason,
        durationMinutes: session.durationMinutes || null,
      }),
      itemsSource: 'live',
    },
    items,
    notices: emptyNotices(),
  };
}

/**
 * Canonical "time used".
 *
 * `duration_minutes` is the time ALLOWED and is never time used. The canonical
 * value is the exam clock the learner actually consumed (allowance minus the
 * countdown's remaining seconds; the countdown is paused during breaks). When a
 * session predates that column we fall back to the sum of per-question timing
 * and label it as an estimate; a timed-out session consumed its whole allowance.
 */
export function deriveTimeUsed({ storedSeconds, items, completionReason, durationMinutes }) {
  if (typeof storedSeconds === 'number' && storedSeconds >= 0) {
    return { timeUsedSeconds: storedSeconds, timeUsedSource: 'exam_clock' };
  }
  if (completionReason === 'expired' && durationMinutes > 0) {
    return { timeUsedSeconds: durationMinutes * 60, timeUsedSource: 'expired_allowance' };
  }
  const summed = (items || []).reduce(
    (acc, i) => (typeof i.timeSpentSeconds === 'number' ? acc + i.timeSpentSeconds : acc), 0,
  );
  if (summed > 0) return { timeUsedSeconds: summed, timeUsedSource: 'question_timing' };
  return { timeUsedSeconds: null, timeUsedSource: null };
}

function emptyNotices() {
  return { reconstructedChanged: 0, unverified: 0, unavailable: 0, keyChanged: 0 };
}

/** Status for a stored item: recorded correctness first, never re-scored. */
export function historicalStatus(raw) {
  if (raw.summary_status) {
    return ['correct', 'incorrect', 'skipped', 'unanswered'].includes(raw.summary_status)
      ? raw.summary_status : 'unanswered';
  }
  if (raw.was_answered === true) return raw.is_correct === true ? 'correct' : 'incorrect';
  if (raw.was_skipped === true) return 'skipped';
  return 'unanswered';
}

/**
 * Historical model — one stored attempt as returned by get_session_review.
 * Returns null for an empty/!object payload so callers can show "not found".
 */
export function buildHistoricalReviewModel(payload) {
  if (!payload || typeof payload !== 'object' || !payload.session) return null;
  const s = payload.session;
  const rawItems = Array.isArray(payload.items) ? payload.items : [];
  const notices = emptyNotices();

  const items = rawItems.map((raw, i) => {
    const snapshot = raw.question && typeof raw.question === 'object' ? raw.question : null;
    const keyAtAttempt = Array.isArray(raw.key_at_attempt) ? raw.key_at_attempt.map(String) : null;
    const currentKey = Array.isArray(raw.current_key) ? raw.current_key.map(String) : null;

    let question = null;
    if (snapshot) {
      question = normalizeSupabaseQuestion(snapshot, s.exam_id);
      // Grade display follows the key the attempt was scored against.
      if (keyAtAttempt) question = { ...question, correctAnswers: keyAtAttempt };
    }

    const keyChanged = !!(keyAtAttempt && currentKey && !sameKey(keyAtAttempt, currentKey));
    const contentStatus = raw.content_status || 'unverified';
    const contentChanged = raw.content_changed_since_attempt === true;

    if (contentStatus === 'unavailable') notices.unavailable += 1;
    else if (contentStatus === 'unverified') notices.unverified += 1;
    else if (contentStatus === 'reconstructed' && contentChanged) notices.reconstructedChanged += 1;
    if (keyChanged) notices.keyChanged += 1;

    return {
      index: typeof raw.order === 'number' ? raw.order : i,
      order: typeof raw.order === 'number' ? raw.order : i,
      question,
      referenceId: raw.reference_id || question?.referenceId || null,
      selectedAnswers: Array.isArray(raw.selected_answers) ? raw.selected_answers.map(String) : [],
      status: historicalStatus(raw),
      isExperimental: raw.is_experimental === true,
      timeSpentSeconds: typeof raw.time_spent_seconds === 'number' ? raw.time_spent_seconds : null,
      wasFlagged: raw.was_flagged === true,
      caseStudyId: raw.case_study_id || null,
      contentStatus,
      contentChanged,
      keyChanged,
      currentKey,
    };
  }).sort((a, b) => a.order - b.order);

  const correct = numOr(s.score_correct, 0);
  const incorrect = numOr(s.score_incorrect, 0);
  const skipped = numOr(s.score_skipped, 0);
  const unanswered = numOr(s.score_unanswered, 0);

  return {
    mode: 'historical',
    summary: {
      sessionId: s.id,
      examId: s.exam_id,
      displayName: s.display_name || null,
      modeName: s.mode_name,
      mode: s.mode_name,
      completionReason: s.completion_reason || 'completed',
      startedAt: s.started_at || null,
      endedAt: s.ended_at || null,
      totalQuestions: numOr(s.total_questions, items.length),
      answered: numOr(s.answered_count, correct + incorrect),
      correct,
      incorrect,
      skipped,
      unanswered,
      // Denominator as stored: experimental questions were already excluded
      // from the scored total when the session was written.
      total: correct + incorrect + skipped + unanswered || numOr(s.total_questions, items.length),
      // STORED score. Never recomputed.
      percentage: numOr(s.score_percentage, null),
      hasExperimental: items.some((i) => i.isExperimental),
      durationMinutes: numOr(s.duration_minutes, null),
      ...deriveTimeUsed({
        storedSeconds: numOr(s.time_used_seconds, null),
        items,
        completionReason: s.completion_reason,
        durationMinutes: numOr(s.duration_minutes, null),
      }),
      itemsSource: s.items_source || 'attempts',
    },
    items,
    notices,
  };
}

function numOr(v, fallback) {
  return typeof v === 'number' && isFinite(v) ? v : fallback;
}
